import React, { Suspense } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';

import App from '../App';
import { ROUTE_NAMES } from './route-names';
import { SessionErrorBoundary } from '../components/ErrorBoundary';
import Loader from '../components/Loader';
import { getPaymentFromLocalStorage } from '../helpers/getPaymentFromLocalStorage';
import type { Payment } from '../models/payment';
import { LocalStorage, PAYMENT_KEY } from '../storage';

// Lazy-loaded screen components for code splitting
const ChargePoint = React.lazy(() => import('../screens/ChargePoint'));
const ChargePointSearch = React.lazy(() => import('../screens/ChargePointSearch'));
const ChargingSession = React.lazy(() => import('../screens/ChargingSession'));
const ChargingSessionFailed = React.lazy(() => import('../screens/ChargingSessionFailed'));
const ChooseLanguage = React.lazy(() => import('../screens/ChooseLanguage'));
const CreatePinCode = React.lazy(() => import('../screens/CreatePinCode'));
const EnterLast4 = React.lazy(() => import('../screens/EnterLast4'));
const EnterPinCode = React.lazy(() => import('../screens/EnterPinCode'));
const HowToSetPinCode = React.lazy(() => import('../screens/HowToSetPinCode'));
const InvoiceEmailConfirmation = React.lazy(() => import('../screens/InvoiceEmailConfirmation'));
const PaymentMethods = React.lazy(() => import('../screens/PaymentMethods'));
const ConfirmWorldLinePayment = React.lazy(() => import('../screens/PaymentMethods/ConfirmWorldLinePayment'));
const PriceForecast = React.lazy(() => import('../screens/PriceForecast'));
const PricingDetails = React.lazy(() => import('../screens/PricingDetails'));
const ReEnterCardDetails = React.lazy(() => import('../screens/ReEnterCardDetails'));
const RequestInvoice = React.lazy(() => import('../screens/RequestInvoice'));
const SelectEvse = React.lazy(() => import('../screens/SelectEvse'));
const SessionSummary = React.lazy(() => import('../screens/SessionSummary'));

const updatePayment = (updates: Partial<Payment>) => {
  const payment = getPaymentFromLocalStorage();

  if (payment) {
    const updatedPayment = {
      ...payment,
      ...updates,
    };

    LocalStorage.setEncryptedItem(PAYMENT_KEY, updatedPayment);
  }
};

const setPaymentId = (paymentIntentId?: string) => {
  updatePayment({ paymentIntentId });
};

const router = createBrowserRouter([
  {
    path: ROUTE_NAMES.root,
    element: <App />,
    children: [
      {
        path: `tenant/:tenantName/${ROUTE_NAMES.chooseLanguage}`,
        element: (
          <Suspense fallback={<Loader preventNavigation={false} />}>
            <ChooseLanguage />
          </Suspense>
        ),
      },
      {
        path: `tenant/:tenantName/${ROUTE_NAMES.search}`,
        element: (
          <Suspense fallback={<Loader preventNavigation={false} />}>
            <ChargePointSearch />
          </Suspense>
        ),
      },
      {
        // SC-620134: Handle charge point session-start URLs: /tenant/:name/session-start/cp/:identifier
        path: `tenant/:tenantName/${ROUTE_NAMES.chargePointSessionStart}/:identifier`,
        element: (
          <Suspense fallback={<Loader preventNavigation={false} />}>
            <ChooseLanguage />
          </Suspense>
        ),
      },
      {
        // Handle session-start URLs with tenant prefix: /tenant/:name/session-start/:evseId
        path: `tenant/:tenantName/${ROUTE_NAMES.sessionStart}/:evsePhysicalReference`,
        element: (
          <Suspense fallback={<Loader preventNavigation={false} />}>
            <ChooseLanguage />
          </Suspense>
        ),
      },
      {
        // Handle legacy session-start URLs: /1/session-start/:evseId
        // The "1" prefix appears to be a legacy tenant identifier
        path: `1/${ROUTE_NAMES.sessionStart}/:evsePhysicalReference`,
        element: (
          <Suspense fallback={<Loader preventNavigation={false} />}>
            <ChooseLanguage />
          </Suspense>
        ),
      },
      {
        // SC-298670: session-start URLs with EVSE context are handled by buildAppRootPath()
        // which consumes the session-start/:evseId prefix before /tenant.
        // The remaining path matches this route directly.
        path: 'tenant/:tenantName/cp/:chargePointId/evse/:evsePhysicalReference/evses',
        element: (
          <Suspense fallback={<Loader preventNavigation={false} />}>
            <SelectEvse />
          </Suspense>
        ),
      },
      {
        path: `tenant/:tenantName/cp/:chargePointId/evse/:evsePhysicalReference/${ROUTE_NAMES.priceForecast}`,
        element: (
          <Suspense fallback={<Loader preventNavigation={false} />}>
            <PriceForecast />
          </Suspense>
        ),
      },
      {
        path: ROUTE_NAMES.priceDetails,
        element: (
          <Suspense fallback={<Loader preventNavigation={false} />}>
            <PricingDetails />
          </Suspense>
        ),
      },
      {
        path: 'tenant/:tenantName/cp/:chargePointId/evse/:evsePhysicalReference',
        element: <Outlet />,
        children: [
          {
            path: ROUTE_NAMES.choosePaymentMethod,
            element: (
              <Suspense fallback={<Loader preventNavigation={false} />}>
                <PaymentMethods />
              </Suspense>
            ),
          },
          {
            path: ROUTE_NAMES.confirmPayment,
            element: (
              <Suspense fallback={<Loader preventNavigation={false} />}>
                <ConfirmWorldLinePayment />
              </Suspense>
            ),
          },
          {
            path: `${ROUTE_NAMES.choosePaymentMethod}/:paymentRef/${ROUTE_NAMES.howToSetPinCode}`,
            loader: async ({ params }) => {
              setPaymentId(params?.paymentRef);

              return params;
            },
            element: (
              <Suspense fallback={<Loader preventNavigation={false} />}>
                <HowToSetPinCode />
              </Suspense>
            ),
          },
          {
            path: `${ROUTE_NAMES.choosePaymentMethod}/:paymentRef/${ROUTE_NAMES.createPinCode}`,
            loader: async ({ params }) => {
              setPaymentId(params?.paymentRef);

              return params;
            },
            element: (
              <SessionErrorBoundary>
                <Suspense fallback={<Loader preventNavigation={false} />}>
                  <CreatePinCode />
                </Suspense>
              </SessionErrorBoundary>
            ),
          },
          {
            path: ROUTE_NAMES.enterPinCode,
            element: (
              <SessionErrorBoundary>
                <Suspense fallback={<Loader preventNavigation={false} />}>
                  <EnterPinCode />
                </Suspense>
              </SessionErrorBoundary>
            ),
          },
          {
            path: ROUTE_NAMES.reEnterCardDetails,
            element: (
              <Suspense fallback={<Loader preventNavigation={false} />}>
                <ReEnterCardDetails />
              </Suspense>
            ),
          },
          {
            path: ROUTE_NAMES.resetPinCode,
            element: (
              <SessionErrorBoundary>
                <Suspense fallback={<Loader preventNavigation={false} />}>
                  <CreatePinCode />
                </Suspense>
              </SessionErrorBoundary>
            ),
          },
          {
            path: `${ROUTE_NAMES.choosePaymentMethod}/:paymentRef/${ROUTE_NAMES.enterLast4Digits}`,
            loader: async ({ params }) => {
              setPaymentId(params?.paymentRef);

              return params;
            },
            element: (
              <Suspense fallback={<Loader preventNavigation={false} />}>
                <EnterLast4 />
              </Suspense>
            ),
          },
          {
            path: `${ROUTE_NAMES.chargingSession}/:sessionId`,
            loader: async ({ params }) => {
              updatePayment({
                sessionId: params?.sessionId,
                paymentIntentId: undefined,
              });

              return params;
            },
            element: <Outlet />,
            children: [
              {
                index: true,
                element: (
                  <SessionErrorBoundary>
                    <Suspense fallback={<Loader preventNavigation={false} />}>
                      <ChargingSession />
                    </Suspense>
                  </SessionErrorBoundary>
                ),
              },
              {
                path: ROUTE_NAMES.sessionSummary,
                element: (
                  <SessionErrorBoundary>
                    <Suspense fallback={<Loader preventNavigation={false} />}>
                      <SessionSummary />
                    </Suspense>
                  </SessionErrorBoundary>
                ),
              },
            ],
          },
          {
            path: ROUTE_NAMES.chargingSessionFailed,
            element: (
              <Suspense fallback={<Loader preventNavigation={false} />}>
                <ChargingSessionFailed />
              </Suspense>
            ),
          },
          {
            path: ROUTE_NAMES.requestInvoice,
            element: (
              <Suspense fallback={<Loader preventNavigation={false} />}>
                <RequestInvoice />
              </Suspense>
            ),
          },
          {
            path: ROUTE_NAMES.confirmInvoiceEmail,
            element: (
              <Suspense fallback={<Loader preventNavigation={false} />}>
                <InvoiceEmailConfirmation />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `tenant/:tenantName/cp/:chargePointId/${ROUTE_NAMES.chargePoint}`,
        element: (
          <Suspense fallback={<Loader preventNavigation={false} />}>
            <ChargePoint />
          </Suspense>
        ),
      },
      {
        path: `tenant/:tenantName/${ROUTE_NAMES.chargePoint}`,
        element: <Outlet />,
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<Loader preventNavigation={false} />}>
                <ChargePoint />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: `tenant/:tenantName/cp/:chargePointId/${ROUTE_NAMES.selectEvse}`,
        element: (
          <Suspense fallback={<Loader preventNavigation={false} />}>
            <SelectEvse />
          </Suspense>
        ),
      },
    ],
  },
]);

export default router;
